<template>
  <el-row>
    <el-col :span="24">
      <h3 class="form-title">
        {{ id ? "Metadatani tahrirlash" : "Metadata qo'shish" }}
      </h3>
    </el-col>
    <el-col :span="24">
      <el-form
        v-loading="loading"
        label-position="top"
        ref="formRef"
        :model="form"
        class="fsm-form"
      >
        <el-form-item
          label="Sarlavha (O'zbekcha lotin)"
          prop="title.uz_lat"
          :rules="[{ required: true, message: 'Sarlavhani kiriting' }]"
        >
          <el-input v-model="form.title.uz_lat" placeholder="O'zbek tilidagi sarlavha" />
        </el-form-item>

        <el-form-item label="Sarlavha (Rus tili)" prop="title.ru">
          <el-input v-model="form.title.ru" placeholder="Rus tilidagi sarlavha" />
        </el-form-item>

        <el-form-item label="Sarlavha (Ingliz tili)" prop="title.en">
          <el-input v-model="form.title.en" placeholder="Ingliz tilidagi sarlavha" />
        </el-form-item>

        <el-form-item label="Sarlavha (O'zbekcha kirill)" prop="title.uz_cyr">
          <el-input v-model="form.title.uz_cyr" placeholder="Kirill yozuvidagi sarlavha" />
        </el-form-item>

        <el-form-item label="Sarlavha (Qoraqalpoqcha)" prop="title.kr">
          <el-input v-model="form.title.kr" placeholder="Qoraqalpoq tilidagi sarlavha" />
        </el-form-item>

        <el-form-item
          label="Fayl (PDF yoki Excel)"
          prop="file_id"
          :rules="[{ required: true, message: 'Faylni yuklang', trigger: 'change' }]"
        >
          <el-input v-model="form.file_id" style="display: none" />
          <input
            type="file"
            ref="fileInputRef"
            hidden
            accept=".pdf,.xls,.xlsx"
            @change="uploadFile"
          />
          <div class="file-upload-row">
            <el-button :loading="fileLoading" @click="fileInputRef.click()">
              {{ form.file_id ? "Faylni almashtirish" : "Fayl yuklash" }}
            </el-button>
            <span v-if="fileName" class="file-name">{{ fileName }}</span>
          </div>
        </el-form-item>

        <div class="form-actions">
          <router-link to="/app/financial-statistic-metadata" class="el-button el-button--danger">
            Bekor qilish
          </router-link>
          <el-button type="primary" :loading="submitLoading" @click="submitForm">
            Saqlash
          </el-button>
        </div>
      </el-form>
    </el-col>
  </el-row>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { ElMessage, ElNotification } from "element-plus";
import http from "@/http";
import store from "@/store";
import router from "@/router";
import { useRoute } from "vue-router";

const route = useRoute();
const { id } = route.params;

const formRef = ref();
const fileInputRef = ref();
const loading = ref(false);
const fileLoading = ref(false);
const submitLoading = ref(false);
const fileName = ref(null);

const form = reactive({
  title: { uz_lat: "", ru: "", en: "", uz_cyr: "", kr: "" },
  file_id: "",
});

const ALLOWED_FILE_EXTENSIONS = ["pdf", "xls", "xlsx"];

const uploadFile = (event) => {
  const file = event.target.files[0];
  if (!file) return;
  const extension = file.name.split(".").pop().toLowerCase();
  if (!ALLOWED_FILE_EXTENSIONS.includes(extension)) {
    ElMessage.error("Faqat PDF yoki Excel (xls, xlsx) fayl yuklash mumkin!");
    event.target.value = "";
    return;
  }
  if (file.size / 1024 / 1024 > 20) {
    ElMessage.error("Fayl xajmi max. 20MB bo'lishi kerak!");
    event.target.value = "";
    return;
  }
  const formData = new FormData();
  formData.append("file", file);
  fileLoading.value = true;
  http
    .post(`${process.env.VUE_APP_BASE_API_URL}api/v2/info/file/upload`, formData, {
      headers: {
        Authorization: store.state.accessToken,
        "Content-Type": "multipart/form-data",
      },
    })
    .then(({ data }) => {
      form.file_id = data.hashId;
      fileName.value = file.name;
      formRef.value?.clearValidate(["file_id"]);
    })
    .catch(() => {
      ElMessage.error("Fayl yuklashda xatolik. Qayta urinib ko'ring!");
    })
    .finally(() => {
      fileLoading.value = false;
      event.target.value = "";
    });
};

const submitForm = () => {
  if (!formRef.value) return;
  formRef.value.validate((valid, fields) => {
    if (!valid) {
      Object.values(fields)
        .flat()
        .forEach((e) => ElMessage.error(e.message));
      return;
    }
    submitLoading.value = true;
    const url = id
      ? `api/v1/financial-statistic-metadata/${id}`
      : "api/v1/financial-statistic-metadata";
    const method = id ? "put" : "post";
    http({ url, data: { title: form.title, file_id: form.file_id }, method })
      .then(() => {
        ElMessage({ type: "success", message: "Muvaffaqqiyatli saqlandi" });
        router.push("/app/financial-statistic-metadata");
      })
      .catch(({ response }) => {
        ElNotification.error({
          message: response?.data?.message || "Xatolik, qayta urinib ko'ring",
        });
      })
      .finally(() => {
        submitLoading.value = false;
      });
  });
};

onMounted(() => {
  if (!id) return;
  loading.value = true;
  http
    .get(`api/v1/financial-statistic-metadata/${id}`)
    .then(({ data }) => {
      form.title.uz_lat = data.title?.uz_lat || "";
      form.title.ru = data.title?.ru || "";
      form.title.en = data.title?.en || "";
      form.title.uz_cyr = data.title?.uz_cyr || "";
      form.title.kr = data.title?.kr || "";
      form.file_id = data.file_id || "";
      fileName.value = data.file_name || null;
    })
    .catch(() => {
      ElMessage.error("Ma'lumot yuklashda xatolik!");
    })
    .finally(() => {
      loading.value = false;
    });
});
</script>

<style scoped lang="scss">
.form-title {
  margin-bottom: 20px;
  font-weight: 600;
}
.fsm-form {
  max-width: 600px;
}
.file-upload-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.file-name {
  color: #606266;
  font-size: 14px;
}
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  a {
    text-decoration: none;
  }
}
</style>
